import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ORIGIN_OPTIONS = [
  { value: "shirley_originated", label: "Self-Originated (Owner-Generated)", credit: "100% quota credit" },
  { value: "company_assigned", label: "Company/Owner-Assigned", credit: "50% quota credit" },
  { value: "tag_work", label: "Tag Work", credit: "Tracked separately (no quota credit)" },
];
const CREDIT = Object.fromEntries(ORIGIN_OPTIONS.map((o) => [o.value, o.credit]));
const ORIGIN_LABEL = Object.fromEntries(ORIGIN_OPTIONS.map((o) => [o.value, o.label]));

function Fld({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

// Audited quota-attribution correction: shows current vs proposed and the exact
// quota-credit change before confirmation. Replaces the old browser prompts.
export function CorrectOriginDialog({ opp, onClose, onDone }) {
  const [proposed, setProposed] = useState("");
  const [reason, setReason] = useState("");
  const [justification, setJustification] = useState("");
  const [saving, setSaving] = useState(false);
  const changed = proposed && proposed !== opp.origin_classification;
  const submit = async (e) => {
    e.preventDefault();
    if (!changed) return toast.error("Choose a different classification to correct to");
    if (!reason.trim()) return toast.error("A correction reason is required (audited)");
    setSaving(true);
    try {
      await api.post(`/opportunities/${opp.id}/correct-origin`, {
        new_classification: proposed, reason: reason.trim(),
        justification: proposed === "shirley_originated" ? justification.trim() || null : null,
      });
      toast.success("Quota attribution corrected (audited)");
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="correct-origin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Correct Quota Attribution</DialogTitle>
          <DialogDescription>Audited correction of the sales-credit classification. It never changes the commission category.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-sm bg-[#F4F4F5] px-3 py-2 text-sm" data-testid="current-origin">
            <span className="text-xs font-bold uppercase text-[#6D6D70]">Current: </span>
            <span className="font-semibold text-[#032852]">{ORIGIN_LABEL[opp.origin_classification] || "Not classified"}</span>
            <span className="ml-1 text-xs text-[#6D6D70]">({CREDIT[opp.origin_classification] || "no quota credit"})</span>
          </div>
          <Fld label="Proposed classification" required>
            <Select value={proposed} onValueChange={setProposed} required>
              <SelectTrigger className="rounded-sm" data-testid="proposed-origin"><SelectValue placeholder="Select classification" /></SelectTrigger>
              <SelectContent>
                {ORIGIN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label} — {o.credit}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          {changed && (
            <p className="rounded-sm bg-[#F59E0B]/10 px-3 py-2 text-xs font-semibold text-[#B45309]" data-testid="credit-change-preview">
              Quota credit changes: {CREDIT[opp.origin_classification] || "no quota credit"} → {CREDIT[proposed]}
            </p>
          )}
          {proposed === "shirley_originated" && (
            <Fld label="Origination justification" required>
              <Textarea required value={justification} onChange={(e) => setJustification(e.target.value)} rows={2}
                className="rounded-sm" data-testid="origin-justification-input" />
            </Fld>
          )}
          <Fld label="Correction reason (audited)" required>
            <Textarea required value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="rounded-sm" data-testid="origin-reason-input" />
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90" data-testid="confirm-correct-origin">
              Confirm correction
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORY_OPTIONS = [
  { value: "fully_developed", label: "Fully Developed Sale — 10% first month, then 1% months 2–12" },
  { value: "assisted", label: "Assisted Sale — 7% first month, then 1% months 2–12" },
  { value: "tag_work", label: "Tag Work — 10% one-time" },
];

// Admin-confirmed commission category (pay plan). Quota credit is untouched.
export function CommissionCategoryDialog({ opp, onClose, onDone }) {
  const [category, setCategory] = useState(opp.commission_category || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Select a commission category");
    setSaving(true);
    try {
      const { data } = await api.post(`/opportunities/${opp.id}/commission-category`,
        { category, note: note.trim() || null });
      toast.success(`Commission category set (audited)${data.backfilled_commissions ? ` — ${data.backfilled_commissions} held commission(s) backfilled` : ""}`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="commission-category-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Set Commission Category</DialogTitle>
          <DialogDescription>
            Pay plan only (rule P6-2026.06). Changing it never changes quota credit — current quota credit stays {CREDIT[opp.origin_classification] || "unassigned"}.
            Eligible renewal pays a one-time 5% of one month's billing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-sm bg-[#F4F4F5] px-3 py-2 text-sm" data-testid="current-category">
            <span className="text-xs font-bold uppercase text-[#6D6D70]">Current: </span>
            <span className="font-semibold text-[#032852]">
              {CATEGORY_OPTIONS.find((c) => c.value === opp.commission_category)?.label || "Needs classification (no commission generates)"}
            </span>
          </div>
          <Fld label="Commission category" required>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger className="rounded-sm" data-testid="category-select"><SelectValue placeholder="Select pay plan" /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Note (optional)">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-sm" data-testid="category-note-input" />
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="confirm-category">
              Save category
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
